// ModalCitizen is a helper for opening modals
// it ensures that only one instance of each modal type exists (that they are singletons)
// and that modals are attached to the DOM as direct children of <body>
//
// When using a Vue component, it must implement a openModal(params) method
// When using with an xhr modal, the backend should respond plain html, without layout, with a .sf-modal root element
//
// Usage (Vue):
//   1) modalCitizen.openModal('new-remark', {ledgerLabel: '03/2018', ledgerId: 55}) - Legacy way of opening a modal, disconnected from render tree / vuex store / vue context
//   2) modalCitizen.openModalPortal('account-mapping-edit-modal') - Component is organically part of the render tree, can be passed down props from parent, vuex store etc.
//   It is then teleported out of Vue context into a specific placeholder div, a direct ancestor of body
//
// Usage (Xhr):
//   modalCitizen.openXhrModal(event, 'edit-reconciliation-text', '/path/to/modal/action')
//   link_to_xhr_modal('edit-reconciliation-text', '/path/to/modal/action')
import {
  ModalCitizenInterface,
  ModalPayloadMap,
  ModalPayload,
  ModalPortalPayloadMap,
  OpenXhrModalOptions,
} from '@app/types'
import {
  AvailableModals,
  ModalPortalHandlerInterface,
  VueModalHandlerInterface,
  XhrModalHandlerInterface,
  DocumentSelectModalHandlerInterface,
  DocumentModalOptions,
} from './types'

import PortalModalHandler from './modal-handlers/portal-modal-handler'
import XhrModalHandler from './modal-handlers/xhr-modal-handler'
import VueModalHandler from './modal-handlers/vue-modal-handler'
import DocumentSelectModalHandler from './modal-handlers/document-select-modal-handler'
export default class ModalCitizen implements ModalCitizenInterface {
  private portalModalHandler: ModalPortalHandlerInterface
  private xhrModalHandler: XhrModalHandlerInterface
  private vueModalHandler: VueModalHandlerInterface
  private documentSelectModalHandler: DocumentSelectModalHandlerInterface

  constructor(availableModals: AvailableModals) {
    this.portalModalHandler = new PortalModalHandler()
    this.xhrModalHandler = new XhrModalHandler()
    this.vueModalHandler = new VueModalHandler(availableModals)
    this.documentSelectModalHandler = new DocumentSelectModalHandler()
  }

  openModal<T extends keyof ModalPayloadMap>(
    modalIdentifier: T,
    params: ModalPayload<T>
  ): void {
    this.vueModalHandler.open(modalIdentifier, params)
  }

  openModalPortal<T extends keyof ModalPortalPayloadMap>(
    modalIdentifier: T,
    params?: ModalPayload<T>
  ): void {
    this.portalModalHandler.open(modalIdentifier, params)
  }

  openXhrModal(
    event: Event,
    modalIdentifier: string,
    url: string,
    options: OpenXhrModalOptions = {}
  ): void {
    this.xhrModalHandler.open(event, modalIdentifier, url, options)
  }

  closeModalXhr(modalIdentifier: string): void {
    this.xhrModalHandler.close(modalIdentifier)
  }

  closeModalPortal<T extends keyof ModalPortalPayloadMap>(): T | null {
    return this.portalModalHandler.close()
  }

  closeModal<T extends keyof ModalPayloadMap>(modalIdentifier: T): void {
    this.vueModalHandler.close(modalIdentifier)
  }

  cleanupXhrModals(): void {
    this.xhrModalHandler.cleanup()
  }

  openDocumentSelectModal({
    firmId,
    companyId,
    options,
  }: DocumentModalOptions): void {
    this.documentSelectModalHandler.open({
      firmId,
      companyId,
      options,
    })
  }

  documentSelectModalOpened(): void {
    this.documentSelectModalHandler.modalOpened()
  }
}
